/**
 * Explore 內嵌「追蹤中」面板（FE-IA-V2-02）。
 * 重用 Following feed capability；不另開後端。
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { socialPostApi } from '../../api/socialPostApi';
import { useAuth } from '../../auth/AuthContext';
import { useTravelTranslation } from '../../app/TravelLocaleContext';
import ExploreGuestGate from '../explore/ExploreGuestGate';
import SocialDiscoveryCard from '../explore/SocialDiscoveryCard';
import {
  appendFollowingFeedItems,
  followingFeedErrorMessage,
  normalizeFollowingFeedPage,
} from './followingFeedModel';
import {
  getFollowingFeedEpoch,
  subscribeFollowingFeedEpoch,
} from './followingFeedRefresh';
import '../explore/explore.css';

export default function FollowingExplorePanel() {
  const { isAuthenticated } = useAuth();
  const { t } = useTravelTranslation();
  const [items, setItems] = useState([]);
  const [nextCursor, setNextCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [phase, setPhase] = useState('loading');
  const [error, setError] = useState('');
  const [epoch, setEpoch] = useState(getFollowingFeedEpoch);
  const generation = useRef(0);

  useEffect(() => subscribeFollowingFeedEpoch(setEpoch), []);

  const load = useCallback(async ({ append = false, cursor = null } = {}) => {
    const requestGeneration = generation.current;
    setPhase(append ? 'loading-more' : 'loading');
    setError('');
    try {
      const payload = await socialPostApi.listFollowing({
        cursor: cursor || undefined,
        limit: 20,
      });
      if (requestGeneration !== generation.current) return;
      const page = normalizeFollowingFeedPage(payload);
      setItems((current) => (append ? appendFollowingFeedItems(current, page.items) : page.items));
      setNextCursor(page.nextCursor);
      setHasMore(page.hasMore);
      setPhase('ready');
    } catch (cause) {
      if (requestGeneration !== generation.current) return;
      setError(followingFeedErrorMessage(cause));
      setPhase('error');
    }
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      setPhase('idle');
      return undefined;
    }
    generation.current += 1;
    setItems([]);
    setNextCursor(null);
    setHasMore(false);
    load();
    return undefined;
  }, [epoch, load, isAuthenticated]);

  function updateEngagement(postId, property, active) {
    const count = property === 'liked' ? 'likeCount' : 'saveCount';
    setItems((current) => current.map((item) => (String(item.postId) !== String(postId) ? item : ({
      ...item,
      engagement: {
        ...item.engagement,
        [property]: active,
        [count]: Math.max(0, item.engagement[count] + (active ? 1 : -1)),
      },
    }))));
  }

  if (!isAuthenticated) {
    return (
      <ExploreGuestGate
        testId="following-guest-wall"
        titleId="following-guest-title"
        titleKey="travel.discoverGuest.followingTitle"
        bodyKey="travel.discoverGuest.followingBody"
      />
    );
  }

  return (
    <div className="following-explore-panel" data-testid="following-explore-panel">
      {phase === 'loading' ? (
        <section className="explore-state" aria-live="polite">
          <div className="explore-spinner" />
          <p>{t('travel.followingFeed.loading')}</p>
        </section>
      ) : null}
      {phase === 'error' && items.length === 0 ? (
        <section className="explore-state explore-state--error" role="alert">
          <h2>{t('travel.followingFeed.errorTitle')}</h2>
          <p>{error}</p>
          <button type="button" onClick={() => load()}>{t('travel.followingFeed.retry')}</button>
        </section>
      ) : null}
      {phase !== 'loading' && phase !== 'error' && items.length === 0 ? (
        <section className="explore-state">
          <h2>{t('travel.followingFeed.emptyTitle')}</h2>
          <p>{t('travel.followingFeed.emptyHint')}</p>
          <Link to="/explore">{t('travel.followingFeed.emptyCta')}</Link>
        </section>
      ) : null}
      {items.length > 0 ? (
        <ul className="explore-cards following-feed-list">
          {items.map((item) => (
            <li key={item.postId}>
              <SocialDiscoveryCard
                item={item}
                returnTo="/explore?tab=following"
                onEngagementChange={updateEngagement}
              />
            </li>
          ))}
        </ul>
      ) : null}
      {error && items.length > 0 ? (
        <div className="explore-inline-error" role="alert">
          {error}
          {' '}
          <button type="button" onClick={() => load({ append: true, cursor: nextCursor })}>
            {t('travel.followingFeed.retryMore')}
          </button>
        </div>
      ) : null}
      {hasMore ? (
        <button
          className="explore-load-more"
          type="button"
          disabled={phase === 'loading-more'}
          onClick={() => load({ append: true, cursor: nextCursor })}
        >
          {phase === 'loading-more' ? t('travel.followingFeed.loadingMore') : t('travel.followingFeed.loadMore')}
        </button>
      ) : null}
    </div>
  );
}
